<template>
  <!-- The Mystic's room (mystic room 2026-10-30, DESIGN.md §2-3): one stage,
       no divider. The portrait (a card that turns), the sky mark, the line and
       the button are one group, centred a little high between the top band and
       a fixed 70px bottom reserve, so every way in puts them at the same
       pixels; only the chrome differs (the ✕, the bar, the price). The ✕ is
       the session header's, drawn over this stage. -->
  <div
    ref="root"
    class="mystic-room"
    :class="{ 'is-arrive': playing, 'is-waiting': waiting, 'is-settled': mode !== 'arrive', 'is-leaving': leaving, 'is-reduced': reduced }"
    data-testid="mystic-room"
    @click="onStageClick"
  >
    <div class="mr-halo" :style="haloStyle" aria-hidden="true"><div class="mr-flame"></div></div>
    <RoomMotes v-if="!reduced" class="mr-motes" :count="phone ? 6 : 9" :style="motesStyle" />
    <div class="mr-veil" aria-hidden="true"></div>
    <RoomCard
      v-if="L"
      :name="name"
      :image="image"
      :initial="initial"
      :front="L.front"
      :back="L.back"
      :flipped="flipped"
      :description="description"
      :rows="rows"
      :signs="signs"
      :data="data"
      :view="cardView"
      :scene="mode"
      :started="started"
      :cue="cue"
      :wide="!phone"
      :reduced="reduced"
      :face="face"
      :day="dayCard"
      @toggle="toggle"
      @unflip="unflip"
      @open-lesson="$emit('open-lesson', $event)"
      @toggle-follow="$emit('toggle-follow')"
      @make-home="$emit('make-home')"
    />
    <div class="mr-door" :style="doorStyle">
      <MysticRoomDoor
        ref="door"
        :name="name"
        :greeting="greeting"
        :price="price"
        :free="free"
        :continuing="continuing"
        :sky="sky"
        :scene="mode"
        :started="started"
        :finished="finished"
        :leaving="leaving"
        :flipped="flipped"
        :cta-shift="flipped && L ? L.ctaYBack - L.ctaY : 0"
        :line-max="L ? L.lineMax : 0"
        :reduced="reduced"
        :day-open="flipped && face === 'day'"
        :glint="glint"
        @day="openDay"
        @get-reading="getReading"
        @for-someone="forSomeone"
        @arrived="arrived = true"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import RoomCard from '@/components/ui/RoomCard.vue'
import RoomMotes from '@/components/ui/RoomMotes.vue'
import MysticRoomDoor from '@/components/ui/MysticRoomDoor.vue'
import { backRows, cueDue, cueSeen, dayGlintDue, dayGlintSeen, roomLayout } from '@/utils/mystic-room'
import { buildSignList } from '@/utils/chart-signs'
import { holdImage, whenDecoded } from '@/utils/held-image'
import { useReducedMotion } from '@/composables/useReducedMotion'

const props = defineProps({
  data: { type: Object, default: null },
  greeting: { type: String, default: '' },
  sky: { type: Object, default: null },
  price: { type: String, default: '' },
  free: { type: Boolean, default: false },
  continuing: { type: Boolean, default: false },
  // 'arrive' | 'settled' | 'none', read once, as the room opens.
  scene: { type: String, default: 'arrive' },
  // The loading overlay has lifted.
  ready: { type: Boolean, default: true },
  leaving: { type: Boolean, default: false },
  // An entry that asked for the About (2026-10-30, the back of the card is
  // the whole About): '' | 'summary' | 'deck' | 'shared'. The card opens
  // turned to it, then says so ('card-opened') so the ask is used once.
  card: { type: String, default: '' },
})
const emit = defineEmits(['get-reading', 'for-someone', 'played', 'layout', 'card-opened', 'open-lesson', 'toggle-follow', 'make-home'])

const reduced = useReducedMotion()
const mode = ref(props.scene)
const root = ref(null)
const door = ref(null)
const L = ref(null)
const W = ref(0)
const phone = ref(typeof window !== 'undefined' ? window.innerWidth < 700 : false)
const flipped = ref(false)
const finished = ref(false)
const arrived = ref(false)
const cardView = ref('')
// The back's face: the About (the portrait turned it) or the day (the sky
// line turned it; holiday line 2026-10-30). Kept through the turn back so the
// back doesn't change under the seeker's eyes.
const face = ref('about')
const dayCard = computed(() => (props.sky && props.sky.day) || null)
const glint = ref(false)

const name = computed(() => (props.data && props.data.name) || '')
const image = computed(() => (props.data && (props.data.avatar_image || props.data.background_image)) || '')
const initial = computed(() => (name.value.trim()[0] || '✦').toUpperCase())

// ── t = 0: the portrait decoded and the loading overlay gone (decode waits at most 1.2s) ──
const decoded = ref(false)
let decodeTimer = null
const started = computed(() => mode.value !== 'arrive' || reduced.value || (props.ready && decoded.value))
const playing = computed(() => mode.value === 'arrive' && started.value && !reduced.value)
const waiting = computed(() => mode.value === 'arrive' && !started.value)
const cue = ref(false)
watch(started, (on) => {
  if (!on) return
  emit('played')
  if (cue.value) cueSeen()
  if (glint.value && dayCard.value) dayGlintSeen(dayCard.value.key, todayIso())
}, { immediate: true })
function todayIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// The portrait is held decoded (utils/held-image), so back from a reading the
// room's new <img> paints at once instead of reloading (walk 2026-10-30, N1).
function decodePortrait () {
  decodeTimer = setTimeout(() => { decoded.value = true }, 1200)
  const img = holdImage(image.value)
  if (!img) { decoded.value = true; return }
  whenDecoded(img).then(() => { decoded.value = true }, () => { decoded.value = true })
}
watch(image, (url) => { holdImage(url) })

// ── the one stage's layout ──
function measure () {
  const el = root.value
  if (!el) return
  phone.value = window.innerWidth < 700
  W.value = el.clientWidth
  const H = el.clientHeight
  const doorH = door.value && door.value.lineHeight ? door.value.lineHeight() : 120
  const next = roomLayout({ W: W.value, H, doorH, phone: phone.value })
  L.value = next
  emit('layout', { ...next, W: W.value, H })
}
let ro = null
onMounted(() => {
  cue.value = mode.value === 'arrive' && !reduced.value && cueDue()
  glint.value = !reduced.value && !!dayCard.value && dayGlintDue(dayCard.value.key, todayIso())
  if (glint.value && started.value) dayGlintSeen(dayCard.value.key, todayIso())
  decodePortrait()
  measure()
  nextTick(() => { measure(); requestAnimationFrame(measure) })
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => measure())
    ro.observe(root.value)
    // The line's own height too: the web font lands after the first measure
    // and re-wraps the line, which moved Get Reading into the turned card.
    const line = door.value && door.value.lineEl ? door.value.lineEl() : null
    if (line) ro.observe(line)
  } else {
    window.addEventListener('resize', measure)
  }
})
onBeforeUnmount(() => {
  clearTimeout(decodeTimer)
  clearTimeout(focusTimer)
  if (ro) ro.disconnect(); else window.removeEventListener('resize', measure)
})
watch(() => props.greeting, () => nextTick(measure))

const dw = computed(() => Math.min(W.value - 36, phone.value ? 560 : 540))
const doorStyle = computed(() => (L.value
  ? { width: `${dw.value}px`, left: `${(W.value - dw.value) / 2}px`, top: `${L.value.doorY}px` }
  : { visibility: 'hidden' }))
const haloStyle = computed(() => {
  if (!L.value) return { display: 'none' }
  const f = L.value.front
  const s = f.w * 2.7
  return { width: `${s}px`, height: `${s}px`, left: `${W.value / 2 - s / 2}px`, top: `${f.y + f.h / 2 - s / 2}px` }
})
const motesStyle = computed(() => {
  if (!L.value) return { display: 'none' }
  const p = L.value.pz
  return { width: `${p * 1.9}px`, height: `${p * 1.3}px`, left: `${W.value / 2 - p * 0.95}px`, top: `${L.value.front.y - p * 0.2}px` }
})

// ── the back of the card: the whole About ──
const description = computed(() => String((props.data && (props.data.reader_description || props.data.teaser)) || '').trim())
const GLYPHS = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}
const rows = computed(() => backRows(props.data, { price: props.free ? '' : props.price }))
// Sun, Moon, Rising, with the signs the Mystic painted in the Studio.
const signs = computed(() => {
  const c = (props.data && props.data.chart) || {}
  return buildSignList(c, { sun: c.sun_image, moon: c.moon_image, rising: c.rising_image })
    .map((s) => ({ role: s.role, value: s.value, img: s.img || '', glyph: `${GLYPHS[s.value] || '✦'}︎` }))
})
// An entry asked for the About: the card is simply there, turned.
watch(() => props.card, (v) => {
  if (!v) return
  cardView.value = v
  face.value = 'about'
  flipped.value = true
  emit('card-opened')
}, { immediate: true })

// ── what a tap does ──
function finishLine () {
  if (mode.value === 'arrive' && started.value && !arrived.value) finished.value = true
}
function onStageClick () {
  finishLine()
}
function toggle () {
  if (props.leaving) return
  finishLine()
  cueSeen()
  cue.value = false
  if (!flipped.value) face.value = 'about'
  flipped.value = !flipped.value
  if (!flipped.value) cardView.value = ''
}
// The sky line on a marked day: the same card, turned to the day.
function openDay () {
  if (props.leaving || !dayCard.value) return
  finishLine()
  glint.value = false
  if (flipped.value && face.value === 'day') { unflip(); return }
  face.value = 'day'
  cardView.value = ''
  flipped.value = true
}
let focusTimer = null
function unflip () {
  const wasDay = flipped.value && face.value === 'day'
  flipped.value = false
  cardView.value = ''
  // Focus returns to the sky line once the card is back.
  if (wasDay) {
    clearTimeout(focusTimer)
    focusTimer = setTimeout(() => { if (door.value && door.value.focusSky) door.value.focusSky() }, reduced.value ? 200 : 600)
  }
}
function getReading () {
  if (props.leaving) return
  flipped.value = false
  emit('get-reading')
}
function forSomeone () {
  if (props.leaving) return
  flipped.value = false
  emit('for-someone')
}
defineExpose({ flipped })
</script>

<style scoped>
.mystic-room {
  position: absolute; inset: 0; z-index: 40; overflow: hidden;
  background: radial-gradient(120% 70% at 50% 34%, #120726 0%, #07030f 62%);
}
.mystic-room.is-settled { animation: mr-in 0.3s ease both; }
@keyframes mr-in { from { opacity: 0; } to { opacity: 1; } }
.mr-door { position: absolute; z-index: 6; pointer-events: none; }
.mr-veil { position: absolute; inset: 0; z-index: 3; background: rgba(3, 1, 8, 0.42); opacity: 0; pointer-events: none; transition: opacity 0.7s cubic-bezier(0.33, 1, 0.68, 1); }
.mystic-room.is-leaving .mr-veil { opacity: 1; }

/* The candle light behind the portrait: swells from 55% on arrival, then flickers very slightly. */
.mr-halo { position: absolute; z-index: 1; pointer-events: none; border-radius: 50%; transition: opacity 0.7s ease; }
.mr-flame {
  position: absolute; inset: 0; border-radius: 50%;
  background: radial-gradient(circle, rgba(250, 214, 140, 0.42) 0%, rgba(240, 190, 110, 0.26) 30%, rgba(214, 150, 92, 0.12) 44%, rgba(138, 95, 214, 0.08) 56%, rgba(0, 0, 0, 0) 70%);
  animation: mr-flicker 5.2s ease-in-out infinite;
}
@keyframes mr-flicker {
  0%, 100% { opacity: 1; transform: scale(1); }
  23% { opacity: 0.9; transform: scale(0.985); }
  41% { opacity: 1; transform: scale(1.01); }
  63% { opacity: 0.93; }
  80% { opacity: 1; transform: scale(0.995); }
}
.mystic-room.is-waiting .mr-halo { opacity: 0; }
.mystic-room.is-arrive .mr-halo { animation: mr-light 1s cubic-bezier(0.33, 1, 0.68, 1) both; }
@keyframes mr-light { from { opacity: 0; transform: scale(0.55); } to { opacity: 1; transform: none; } }
.mystic-room.is-leaving .mr-halo { opacity: 0.7; }
.mr-motes { z-index: 2; transition: opacity 0.7s ease; }
.mystic-room.is-waiting .mr-motes { opacity: 0; }
.mystic-room.is-arrive .mr-motes { animation: mr-motes-in 1.2s cubic-bezier(0.33, 1, 0.68, 1) 0.5s both; }
@keyframes mr-motes-in { from { opacity: 0; } to { opacity: 1; } }
.mystic-room.is-leaving .mr-motes { opacity: 0.5; }

.mystic-room.is-reduced, .mystic-room.is-reduced .mr-halo, .mystic-room.is-reduced .mr-flame { animation: none !important; }
.mystic-room.is-reduced .mr-veil { transition: none; }
</style>
